import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLightbulb } from "@fortawesome/free-solid-svg-icons";
import Label from "../shared-components/Label";
import Select from "../shared-components/Select";
import Text from "../shared-components/Text";
import DatePicker from "../shared-components/DatePicker";
import FormActionButton from "../shared-components/FormActionButton";
import { formPages } from "../../constants";
import "./NewClaimantDetails.css";

export default function NewClaimantDetails({ compensate, onNext }) {
	const [title, setTitle] = useState(compensate.title);
	const [firstName, setFirstName] = useState(compensate.firstName ?? "");
	const [middleName] = useState(compensate.middleName ?? ""); // setMiddleName removed - field hidden
	const [surname, setSurname] = useState(compensate.surname ?? "");
	const [dob, setDob] = useState(compensate.dob ?? "");
	const [previousName, setPreviousName] = useState(
		compensate.previousName ?? "",
	);

	// const [title, setTitle] = useState("");
	// const [firstName, setFirstName] = useState("");
	// const [surname, setSurname] = useState("");
	// const [dob, setDob] = useState("");
	// const [previousName, setPreviousName] = useState("");

	const [titleError, setTitleError] = useState(false);
	const [fnameError, setFNameError] = useState(false);
	const [lnameError, setLnameError] = useState(false);
	const [dobError, setDobError] = useState(false);
	const [previousNameError, setPreviousNameError] = useState(false);
	// const [middleNameError, setMiddleNameError] = useState(false); // Hidden field

	// Function to check if all mandatory fields are filled
	const isFormValid = () => {
		// More robust date validation - check for proper date format
		const isValidDate =
			dob && dob !== "" && dob.includes("/") && dob.split("/").length === 3;

		// Validate previous name only if it's not empty (since it's optional)
		const isPreviousNameValid =
			previousName === "" || isValidName(previousName);
		// Validate middle name only if it's not empty (since it's optional)
		const isMiddleNameValid = middleName === "" || isValidName(middleName);

		return (
			title &&
			title !== "" &&
			firstName &&
			firstName !== "" &&
			isValidName(firstName) &&
			surname &&
			surname !== "" &&
			isValidName(surname) &&
			isValidDate &&
			isPreviousNameValid &&
			isMiddleNameValid
		);
	};

	const titleOptions = [
		{ text: "Mr", value: "MR" },
		{ text: "Mrs", value: "MRS" },
		{ text: "Ms", value: "MS" },
		{ text: "Miss", value: "MISS" },
		{ text: "Other", value: "MX" },
	];

	// Function to validate name format (only letters, spaces, hyphens, apostrophes)
	const isValidName = (name) => {
		const nameRegex = /^[a-zA-Z\s\-']+$/;
		return nameRegex.test(name);
	};

	const toNameCase = (str) =>
		str.length === 0 ? str : str.charAt(0).toUpperCase() + str.slice(1);

	const fname = (event) => {
		const val = toNameCase(event);
		setFirstName(val);
		if (val === "") {
			setFNameError(true);
		} else if (!isValidName(val)) {
			setFNameError(true);
		} else {
			setFNameError(false);
		}
	};

	const lname = (event) => {
		const val = toNameCase(event);
		setSurname(val);
		if (val === "") {
			setLnameError(true);
		} else if (!isValidName(val)) {
			setLnameError(true);
		} else {
			setLnameError(false);
		}
	};

	const plname = (event) => {
		const val = toNameCase(event);
		setPreviousName(val);
		// Only validate if field is not empty (since it's optional)
		if (val !== "" && !isValidName(val)) {
			setPreviousNameError(true);
		} else {
			setPreviousNameError(false);
		}
	};

	// const mname = (event) => {
	//   setMiddleName(event);
	//   // Only validate if field is not empty (since it's optional)
	//   if (event !== "" && !isValidName(event)) {
	//     setMiddleNameError(true);
	//   } else {
	//     setMiddleNameError(false);
	//   }
	// };

	return (
		<>
			<div
				className="d-flex justify-content-center align-items-center heading-icon fdc-form"
				data-testid="page-claimant"
				style={{
					paddingTop: "8px",
					paddingBottom: "8px",
				}}
			>
				<div className="row">
					<h2 className="title">
						We just need a few details to get you started!
					</h2>
					<p className="household-amount-text" style={{ fontSize: "1.2rem" }}>
						<span style={{ fontWeight: "700" }}>You could be owed</span>{" "}
						<span
							className="household-amount-value"
							style={{
								color: "var(--brand-primary)",
								fontSize: "1.2rem",
								fontWeight: "700",
								fontFamily: "var(--current-font)",
								fontVariantNumeric: "lining-nums tabular-nums",
							}}
						>
							£2,086*
						</span>{" "}
						<span style={{ fontWeight: "700" }}>per household!</span>
					</p>
					<div className="col-4 col-md-4">
						<div className="form-block">
							<Select
								label={"Title"}
								options={titleOptions}
								onChange={(value) => {
									setTitle(value);
									if (value && value !== "") {
										setTitleError(false);
									}
								}}
								value={title}
								id="Title"
								placeholder={"Select"}
								testId="claimant-title"
							/>
							{titleError ? (
								<div className="error-message">
									<p>Please select a title</p>
								</div>
							) : (
								<></>
							)}
						</div>
					</div>
					<div className="col-8 col-md-8">
						<div className="form-block">
							<Text
								label={"What is your first name?"}
								onChange={(e) => fname(e.target.value)}
								value={firstName}
								id="First Name"
								placeholder={"Enter"}
								testId="claimant-first-name"
							/>
							{fnameError ? (
								<div className="error-message">
									<p>
										{firstName === ""
											? "Please enter your first name"
											: "Please enter a valid first name (letters, spaces, hyphens, and apostrophes only)"}
									</p>
								</div>
							) : (
								<></>
							)}
						</div>
					</div>
					<div className="col-md-6">
						<div className="form-block">
							<Text
								label={"What is your surname?"}
								onChange={(e) => lname(e.target.value)}
								value={surname}
								id="Surname"
								placeholder={"Enter"}
								testId="claimant-surname"
							/>

							{lnameError ? (
								<div className="error-message">
									<p>
										{surname === ""
											? "Please enter your surname"
											: "Please enter a valid surname (letters, spaces, hyphens, and apostrophes only)"}
									</p>
								</div>
							) : (
								<></>
							)}
						</div>
					</div>
					{/* Middle name field hidden as per requirement */}
					{/* <div className="col-md-6">
            <div className="form-block">
              <Text
                label={<>What is your middle name? <i>(if applicable)</i></>}
                onChange={(e) => mname(e.target.value)}
                value={middleName}
                id="Middle Name"
                placeholder={"Enter"}
                required={false}
              />
              {middleNameError ? (
                <div className="error-message">
                  <p>Please enter a valid middle name (letters, spaces, hyphens, and apostrophes only)</p>
                </div>
              ) : (
                <></>
              )}
            </div>
          </div> */}
					<div className="col-md-6">
						<div className="form-block">
							<Text
								label={
									<>
										Do you have a previous surname? <i>(if applicable)</i>
									</>
								}
								onChange={(e) => plname(e.target.value)}
								value={previousName}
								id="previousSurname"
								placeholder={"Enter"}
								required={false}
								testId="claimant-previous-surname"
							/>
							{previousNameError ? (
								<div className="error-message">
									<p>
										Please enter a valid previous surname (letters, spaces,
										hyphens, and apostrophes only)
									</p>
								</div>
							) : (
								<></>
							)}
						</div>
					</div>
					<div className="col-md-12">
						<div className="d-flex align-items-start gap-2 form-block info-box">
							<img
								src="/icons/lucid-icons/badge-alert.svg"
								alt="alert"
								style={{ width: "24px", height: "24px", flexShrink: 0 }}
							/>
							<span>
								Please enter your names in full to enable the free check.
							</span>
						</div>
					</div>
					<div className="col-md-12">
						<div className="form-block">
							<DatePicker
								label={
									"We use your date of birth to complete the free check to identify vehicle finance claims."
								}
								onChange={(formattedDate) => {
									setDob(formattedDate);
									if (formattedDate && formattedDate !== "") {
										setDobError(false);
									}
								}}
								// moreInfo={
								//   "For example 13/06/1990"
								// }
								value={dob}
								id="Date of birth"
								dateOfBirth
								compensate={compensate}
								testId="claimant-dob"
							/>
							{dobError ? (
								<div className="error-message">
									<p>Please enter your date of birth</p>
								</div>
							) : (
								<></>
							)}
						</div>
					</div>
					<div className="col-md-6 text-center mx-auto">
						<FormActionButton
							nextTestId="claimant-btn-next"
							onNext={() => {
								if (title === "" || title === undefined) {
									setTitleError(true);
								}
								if (
									firstName === "" ||
									firstName === undefined ||
									!isValidName(firstName)
								) {
									setFNameError(true);
								}
								if (
									surname === "" ||
									surname === undefined ||
									!isValidName(surname)
								) {
									setLnameError(true);
								}
								if (dob === "" || dob === undefined) {
									setDobError(true);
								}
								// Validate previous name if it's not empty
								if (previousName !== "" && !isValidName(previousName)) {
									setPreviousNameError(true);
								}
								// Validate middle name if it's not empty - HIDDEN FIELD
								// if (middleName !== "" && !isValidName(middleName)) {
								//   setMiddleNameError(true);
								// }

								if (isFormValid()) {
									onNext(
										{
											title,
											firstName,
											middleName,
											surname,
											dob,
											previousName,
											claims: [],
										},
										formPages.ContactDetails,
									);
								}
							}}
						/>
						<br />
					</div>
					<div className="col-md-12">
						<div className="d-flex align-items-center justify-content-center form-block mb-3 info-box">
							<FontAwesomeIcon
								icon={faLightbulb}
								style={{
									fontSize: "22px",
									color: "var(--ycm-orange)",
									flexShrink: 0,
									marginRight: "8px",
								}}
							/>
							<Label label={"Make your claim in 60 seconds"} />
						</div>
					</div>
					<p className="terms text-center" style={{ fontSize: "16px" }}>
						{/* *Refund value is based on 2 adults per household, an average of 3.4
            finance agreements per person, an FCA estimation of 37% mis-sold, at
            £829 redress per claim. */}
						*Household refund values are based on data published by the Office
						for National Statistics (ONS) and the Financial Conduct Authority
						(FCA), together with analysis of vehicle finance agreements
						identified through our process. Calculations assume 2 adults per
						household and 3.4 vehicle finance agreements per person. FCA data
						indicates average compensation of £829 per agreement and that 37% of
						agreements may be affected. Eligibility and compensation will vary
						and are not guaranteed.
					</p>
				</div>
			</div>
		</>
	);
}
